<template>
  <div :class="['alert', colorClasses, $attrs.class]">
    <div
      v-if="$slots.icon || icon"
      class="alert-icon"
    >
      <slot name="icon">
        <app-icon
          :name="icon"
          :variant="variant"
          :color="color"
          size="lg"
          :class="{ 'px-3 py-2': variant === 'basic' }"
        />
      </slot>
    </div>
    <div class="alert-body">
      <div class="alert-content">
        <p
          v-if="heading"
          class="alert-heading"
        >
          {{ heading }}
        </p>
        <slot />
      </div>
      <div
        v-if="$slots.actions"
        class="alert-actions"
      >
        <slot name="actions" />
      </div>
    </div>
    <button
      v-if="closable"
      class="alert-close-btn"
      @click="$emit('close')"
    >
      <app-icon name="close" />
    </button>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { semanticColors } from '@/constants/colors';

defineOptions({
  name: 'AppAlert',
});

const props = defineProps({
  heading: {
    type: String,
    default: '',
  },
  color: {
    type: String,
    default: 'neutral',
    validator: (value) => semanticColors.includes(value),
  },
  variant: {
    type: String,
    default: 'outlined',
  },
  icon: {
    type: String,
    required: false,
  },
  closable: {
    type: Boolean,
    default: false,
  },
});

const colorClasses = computed(() => {
  return {
    neutral: 'alert-neutral',
    success: 'alert-success',
    warning: 'alert-warning',
    error: 'alert-error',
    primary: 'alert-primary',
  }[props.color];
});

defineEmits(['close']);
</script>

<style scoped>
@reference '@/assets/main.css';

@layer utilities {
  .alert {
    --border-color: var(--color-gray-200);

    @apply relative flex gap-2 border bg-white border-[var(--border-color)] rounded-xl p-4 shadow-xs;

    &.alert-success {
      --border-color: var(--color-green-300);
    }

    &.alert-warning {
      --border-color: var(--color-yellow-300);
    }

    &.alert-error {
      --border-color: var(--color-red-300);
    }

    &.alert-primary {
      --border-color: var(--color-brand-300);
    }
  }

  .alert-close-btn {
    @apply btn btn-square btn-ghost btn-sm absolute right-2 top-2;
  }

  .alert-icon {
    @apply -mt-3 -ml-3;
  }

  .alert-body {
    @apply flex flex-col gap-3;
  }

  .alert-heading {
    @apply text-gray-700 font-semibold mb-1;
  }

  .alert-content {
    @apply text-sm text-gray-600;
  }

  .alert-actions {
    @apply flex gap-2 mt-3;
  }
}
</style>
